import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { PLATFORM_FEE_PERCENT } from '../../lib/stripe';
import NotificationSettings from '../../components/NotificationSettings';

const ChurchSettingsPage = () => {
  const { user, profile } = useAuth();
  const { church, setChurch } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [churchForm, setChurchForm] = useState({
    name: '',
    pastor_name: '',
    city: '',
    state: '',
    phone: '',
    email: '',
    website_url: '',
    address: ''
  });

  useEffect(() => {
    if (church) {
      setChurchForm({
        name: church.name || '',
        pastor_name: church.pastor_name || '',
        city: church.city || '',
        state: church.state || '',
        phone: church.phone || '',
        email: church.email || '',
        website_url: church.website_url || '',
        address: church.address || ''
      });
    }
  }, [church]);

  useEffect(() => {
    const stripeStatus = searchParams.get('stripe');
    if (stripeStatus === 'connected') {
      checkStripeStatus();
    } else if (stripeStatus === 'error') {
      setMessage({ type: 'error', text: 'Failed to connect Stripe account. Please try again.' });
    }
  }, [searchParams]);

  const checkStripeStatus = async () => {
    if (!church?.id) return;

    try {
      const response = await fetch('/.netlify/functions/stripe-check-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ churchId: church.id }),
      });

      const data = await response.json();

      if (data.onboardingComplete) {
        const { data: updatedChurch } = await supabase
          .from('churches')
          .select('*')
          .eq('id', church.id)
          .single();

        if (updatedChurch) {
          setChurch(updatedChurch);
        }
        setMessage({ type: 'success', text: 'Stripe account connected successfully! You can now accept paid registrations.' });
      } else {
        setMessage({ type: 'error', text: 'Stripe onboarding incomplete. Please click "Connect Stripe" to finish setup.' });
      }
    } catch (err) {
      console.error('Error checking Stripe status:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setChurchForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (church?.id) {
        const { error } = await supabase
          .from('churches')
          .update(churchForm)
          .eq('id', church.id);

        if (error) throw error;
        setChurch(prev => ({ ...prev, ...churchForm }));
      } else {
        const { data, error } = await supabase
          .from('churches')
          .insert({
            ...churchForm,
            owner_id: user.id
          })
          .select()
          .single();

        if (error) throw error;
        setChurch(data);
      }

      setMessage({ type: 'success', text: 'Church profile saved successfully!' });
    } catch (err) {
      console.error('Error saving church profile:', err);
      setMessage({ type: 'error', text: 'Failed to save church profile.' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    if (!church?.id) {
      setMessage({ type: 'error', text: 'Please save your church profile first.' });
      return;
    }

    setConnectingStripe(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch('/.netlify/functions/stripe-create-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ churchId: church.id, userId: user.id }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create Stripe account');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Stripe Connect error:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to connect Stripe. Please try again.' });
    } finally {
      setConnectingStripe(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your church profile and payment settings.
        </p>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`p-4 rounded-xl ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Church Profile */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Church Profile</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This information will be displayed on your events.
          </p>
        </div>

        <form onSubmit={handleSaveProfile} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Church Name *
              </label>
              <input
                type="text"
                name="name"
                required
                value={churchForm.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="First Pentecostal Church"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pastor Name
              </label>
              <input
                type="text"
                name="pastor_name"
                value={churchForm.pastor_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Pastor John Smith"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={churchForm.address}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={churchForm.city}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="St. Louis"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                State
              </label>
              <input
                type="text"
                name="state"
                value={churchForm.state}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="MO"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={churchForm.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={churchForm.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="info@yourchurch.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Website
            </label>
            <input
              type="url"
              name="website_url"
              value={churchForm.website_url}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="https://yourchurch.com"
            />
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>

      {/* Payment Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Settings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Connect your bank account to accept paid event registrations.
          </p>
        </div>

        <div className="p-6">
          {church?.stripe_onboarding_complete ? (
            <div className="space-y-4">
              <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-green-800 dark:text-green-300">Stripe Connected</h4>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Your bank account is connected and ready to receive payments.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your Share</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{100 - PLATFORM_FEE_PERCENT}%</p>
                  <p className="text-xs text-gray-400">of each registration</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Platform Fee</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{PLATFORM_FEE_PERCENT}%</p>
                  <p className="text-xs text-gray-400">processing & platform</p>
                </div>
              </div>

              <button
                onClick={handleConnectStripe}
                disabled={connectingStripe}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                Update Stripe Settings →
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                <svg className="w-6 h-6 text-yellow-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-300">Payments Not Configured</h4>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                    Connect your Stripe account to accept paid registrations for your events.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Accept Paid Registrations
                </h3>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Receive {100 - PLATFORM_FEE_PERCENT}% of each registration fee
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Automatic deposits to your bank account
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Secure payment processing by Stripe
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Set early bird pricing and registration limits
                  </li>
                </ul>

                <button
                  onClick={handleConnectStripe}
                  disabled={connectingStripe || !church?.id}
                  className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                >
                  {connectingStripe ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Connect with Stripe
                    </>
                  )}
                </button>

                {!church?.id && (
                  <p className="text-xs text-gray-500 mt-2">
                    Please save your church profile first before connecting Stripe.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notification Settings */}
      <NotificationSettings 
        userId={user.id}
        userType="church"
        settings={profile?.notification_settings}
        onSave={(settings) => {
          // Update profile with new notification settings
          supabase
            .from('profiles')
            .update({ notification_settings: settings })
            .eq('id', user.id)
            .then(({ error }) => {
              if (!error) {
                setMessage({ type: 'success', text: 'Notification settings updated!' });
                setTimeout(() => setMessage({ type: '', text: '' }), 3000);
              }
            });
        }}
      />

      {/* Account Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Account Information</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
              <p className="text-gray-900 dark:text-white font-medium">{user?.email}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Account Type</label>
              <p className="text-gray-900 dark:text-white font-medium capitalize">{profile?.role || 'Church Admin'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Name</label>
              <p className="text-gray-900 dark:text-white font-medium">{profile?.full_name || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Member Since</label>
              <p className="text-gray-900 dark:text-white font-medium">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChurchSettingsPage;
